import { Box, Typography, useTheme } from '@mui/material';
import { CSSProperties } from 'react';

import { Outline } from '../../components/layout/Outline';
import { Children, Component, ReturnComponent } from '../../utils/reactTypes';
import { AxleLabel } from '../formFields/AxleLabel';
import { DangerousHtmlTypography } from './DangerousHtmlTypography';

export const NONE_TEXT = 'None';

type PotentiallyHtmlValue =
    | {
          isValueDangerousHtml?: true;
          value?: string;
      }
    | {
          isValueDangerousHtml?: false;
          value?: Component | undefined;
      };

export const AxleInfoField = ({
    label,
    wrapInOutline = false,
    padding = undefined,
    value: valueRaw = undefined,
    isValueDangerousHtml: isValueDangerousHtmlRaw = false,
    isValueBig = false,
    children = undefined,
    description = undefined,
    color = undefined,
    customPadding = undefined,
}: {
    label: string;
    wrapInOutline?: boolean;
    padding?: number;
    isValueBig?: boolean;
    children?: Children;
    description?: Children;
    color?: string;
    customPadding?: number;
} & PotentiallyHtmlValue): ReturnComponent => {
    const theme = useTheme();

    // Typescript loses type information about discriminated unions when destructuring
    //  them in the function argument (see https://stackoverflow.com/a/69033664). Hence,
    //  we have to do this type assertion and then destructure it again for typing to work.
    const { value, isValueDangerousHtml } = {
        value: valueRaw,
        isValueDangerousHtml: isValueDangerousHtmlRaw,
    } as PotentiallyHtmlValue;

    const valueStyle: CSSProperties = isValueBig
        ? { fontWeight: 'bold', fontSize: '1.25em', whiteSpace: 'pre-wrap' }
        : { whiteSpace: 'pre-wrap' };

    let valueElement;
    if (value === undefined) {
        // Need to reserve some space between the label and the children
        //  if there is no value element in the middle.
        valueElement = <Box sx={{ height: theme.spacing(0.5) }} />;
    } else if (isValueDangerousHtml) {
        // Can dangerously render HTML stored in a string.
        valueElement = (
            <DangerousHtmlTypography style={valueStyle}>{value}</DangerousHtmlTypography>
        );
    } else {
        // Or can render any other type of React node.
        valueElement = (
            <Typography style={valueStyle} component='div'>
                {value}
            </Typography>
        );
    }

    let elementToRender = (
        <>
            {valueElement}
            {children}
        </>
    );

    if (padding) elementToRender = <Box p={padding}>{elementToRender}</Box>;
    if (wrapInOutline)
        elementToRender = (
            <Outline sx={{ px: 2 }} padding={customPadding}>
                {elementToRender}
            </Outline>
        );

    return (
        <Box mb={2}>
            <AxleLabel
                label={label}
                style={{
                    color: color ? color : 'black',
                    paddingBottom: theme.spacing(0.5),
                }}
                description={description}
            />
            {elementToRender}
        </Box>
    );
};
